import React, { useEffect, useState, useCallback } from "react";
import { getProspects, updateStatus } from "../lib/api";
import { STAGES, eur } from "../lib/constants";
import { ScoreBadge } from "../components/Badges";
import { User, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Pipeline({ onOpenProspect, refreshKey }) {
  const [prospects, setProspects] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const load = useCallback(() => getProspects().then(setProspects).catch((e) => console.warn("pipeline load failed", e)), []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const onDrop = async (stageKey) => {
    setOverCol(null);
    const id = dragId;
    setDragId(null);
    if (!id) return;
    const p = prospects.find((x) => x.id === id);
    if (!p || p.status === stageKey) return;
    setProspects((prev) => prev.map((x) => (x.id === id ? { ...x, status: stageKey } : x)));
    try {
      await updateStatus(id, stageKey);
      toast.success(`${p.azienda} → ${STAGES.find((s) => s.key === stageKey).label}`);
    } catch {
      toast.error("Errore aggiornamento stato");
      load();
    }
  };

  return (
    <div className="fade-in flex h-screen flex-col p-6 md:p-8">
      <div className="mb-6 shrink-0">
        <div className="label-caps text-primary">07 · Convert & Follow-up</div>
        <h1 className="mt-1 font-heading text-4xl font-bold tracking-tight">Sales Pipeline</h1>
        <p className="mt-1 text-sm text-muted-foreground">Trascina i prospect tra le fasi. Ogni lead deve avere una prossima azione.</p>
      </div>

      <div className="kanban-scroll flex flex-1 gap-4 overflow-x-auto pb-4">
        {STAGES.map((stage) => {
          const cards = prospects.filter((p) => p.status === stage.key);
          const value = cards.reduce((s, c) => s + (Number(c.valore_potenziale) || 0), 0);
          return (
            <div key={stage.key}
              data-testid={`kanban-column-${stage.key.toLowerCase()}`}
              onDragOver={(e) => { e.preventDefault(); setOverCol(stage.key); }}
              onDragLeave={() => setOverCol((c) => (c === stage.key ? null : c))}
              onDrop={() => onDrop(stage.key)}
              className={`flex w-72 shrink-0 flex-col rounded-xl border border-border bg-white/[0.02] transition-colors ${overCol === stage.key ? "drag-over" : ""}`}
            >
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ background: stage.color }} />
                  <span className="text-sm font-semibold">{stage.label}</span>
                  <span className="rounded-full bg-secondary px-1.5 text-xs text-muted-foreground tabular-nums">{cards.length}</span>
                </div>
                {value > 0 && <span className="text-xs text-muted-foreground">{eur(value)}</span>}
              </div>

              <div className="flex-1 space-y-2 overflow-y-auto p-3">
                {cards.map((p) => (
                  <div key={p.id}
                    draggable
                    onDragStart={() => setDragId(p.id)}
                    onDragEnd={() => setDragId(null)}
                    onClick={() => onOpenProspect(p)}
                    data-testid={`kanban-card-${p.id}`}
                    className={`card-lift cursor-pointer rounded-lg border border-border bg-card p-3 ${dragId === p.id ? "opacity-40" : ""}`}
                  >
                    <div className="mb-2 flex items-start justify-between gap-2">
                      <div className="min-w-0 text-sm font-medium leading-tight">{p.azienda}</div>
                      <ScoreBadge score={p.score} priority={p.priority} size="sm" />
                    </div>
                    {p.servizio_consigliato && (
                      <div className="mb-2 inline-block rounded bg-primary/10 px-1.5 py-0.5 text-[0.65rem] text-primary">{p.servizio_consigliato}</div>
                    )}
                    {p.next_action && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <ArrowRight className="h-3 w-3" /> {p.next_action}
                      </div>
                    )}
                    <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{p.responsabile || "—"}</span>
                      {p.next_action_date && <span>{p.next_action_date.slice(0, 10)}</span>}
                    </div>
                  </div>
                ))}
                {cards.length === 0 && <div className="rounded-lg border border-dashed border-border py-6 text-center text-xs text-muted-foreground">Vuoto</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
